/// <reference types="react-scripts" />

import axios from 'axios';
import {
  User,
  GoogleAuthUrlResponse,
  SettingsResponse,
  SettingUpdate,
  APILogResponse,
  ServiceStatus,
  ServiceData,
  BatteryControlSchedule,
  BatteryRecommendation,
  AlertsResponse
} from '../types/api';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add timing to track API request performance
api.interceptors.request.use(
  (config: any) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add timing metadata
    config.metadata = { startTime: Date.now() };
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response: any) => {
    // Log request timing
    const duration = Date.now() - response.config.metadata.startTime;
    console.log(`🌐 API ${response.config.method?.toUpperCase()} ${response.config.url} completed in ${duration}ms`);    
    return response;
  },
  async (error: any) => {
    // Log failed request timing
    if (error.config?.metadata?.startTime) {
      const duration = Date.now() - error.config.metadata.startTime;
      console.error(`❌ API ${error.config.method?.toUpperCase()} ${error.config.url} failed after ${duration}ms:`, error.message);
    }
    
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      console.log('401 error detected, attempting token refresh...');
      
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          console.log('Refreshing token with:', refreshToken.substring(0, 20) + '...');
          const response = await apiService.refreshToken(refreshToken);
          console.log('Token refresh response:', response);
          
          const newAccessToken = response.access_token;
          const newRefreshToken = response.refresh_token;
          
          // Update tokens in localStorage
          localStorage.setItem('access_token', newAccessToken);
          if (newRefreshToken) {
            localStorage.setItem('refresh_token', newRefreshToken);
          }
          
          // Update axios default headers
          api.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;
          
          // Retry the original request
          originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
          console.log('Retrying original request with new token');
          return api(originalRequest);
        } catch (refreshError) {
          // Refresh failed, redirect to login
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user');
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      } else {
        // No refresh token, redirect to login
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export async function getServiceStatus(): Promise<ServiceStatus[]> {
  const response = await api.get<ServiceStatus[]>('/services/status');
  return response.data;
}

export async function getAmberData(): Promise<ServiceData> {
  const response = await api.get<ServiceData>('/services/amber');
  return response.data;
}

export async function getSolarWebData(): Promise<ServiceData> {
  const response = await api.get<ServiceData>('/services/solarweb');
  return response.data;
}

export async function getSolcastData(): Promise<ServiceData> {
  const response = await api.get<ServiceData>('/services/solcast');
  return response.data;
}

export async function getOpenMeteoData(): Promise<ServiceData> {
  const response = await api.get<ServiceData>('/services/openmeteo');
  return response.data;
}

export async function getCurrentUser(): Promise<User> {
  const response = await api.get<User>('/users/me');
  return response.data;
}

export async function resetSettings(): Promise<any> {
  const response = await api.post('/settings/reset');
  return response.data;
}

export async function logout(): Promise<void> {
  await api.post('/auth/logout');
}

export async function refreshToken(refreshToken: string): Promise<any> {
  const response = await api.post('/auth/refresh', { refresh_token: refreshToken });
  return response.data;
}

export async function getSchedulerStatus(): Promise<any> {
  const response = await api.get<any>('/scheduler/status');
  return response.data;
}

export async function triggerSchedulerJob(): Promise<any> {
  const response = await api.post<any>('/scheduler/trigger');
  return response.data;
}

// Grid Export Control API
export async function getGridExportStatus(): Promise<any> {
  const response = await api.get<any>('/grid-export/status');
  return response.data;
}

export async function enableGridExportControl(): Promise<any> {
  const response = await api.post<any>('/grid-export/enable');
  return response.data;
}

export async function disableGridExportControl(): Promise<any> {
  const response = await api.post<any>('/grid-export/disable');
  return response.data;
}

export async function triggerGridExportControl(): Promise<any> {
  const response = await api.post<any>('/grid-export/trigger');
  return response.data;
}

export async function getGoogleAuthUrl(): Promise<GoogleAuthUrlResponse> {
  const response = await api.get<GoogleAuthUrlResponse>('/auth/google/url');
  return response.data;
}

export async function getApiLogs(limit: number, offset: number): Promise<APILogResponse[]> {
  const response = await api.get<APILogResponse[]>(`/logs?limit=${limit}&offset=${offset}`);
  return response.data;
}

export async function getSettings(): Promise<SettingsResponse> {
  const response = await api.get<SettingsResponse>('/settings');
  return response.data;
}

export async function updateSetting(key: string, setting: any): Promise<SettingUpdate> {
  const response = await api.put<SettingUpdate>(`/settings/${key}`, setting);
  return response.data;
}

export interface SettingsCompleteResponse {
  is_complete: boolean;
  missing_settings: string[];
  has_credentials: boolean;
}

export async function checkSettingsComplete(): Promise<SettingsCompleteResponse> {
  const response = await api.get<SettingsCompleteResponse>('/settings/check-complete');
  return response.data;
}

export async function getLatestUserData(): Promise<any> {
  const response = await api.get<any>('/users/me/latest-data');
  return response.data;
}

export async function getDataVersion(): Promise<any> {
  const response = await api.get<any>('/users/me/data-version');
  return response.data;
}

export async function getUserDataEntries(limit: number = 50): Promise<any> {
  const response = await api.get<any>(`/users/me/data-entries?limit=${limit}`);
  return response.data;
}

export async function getUserDataById(dataId: number): Promise<any> {
  const response = await api.get<any>(`/users/me/data/${dataId}`);
  return response.data;
}

export async function recalculateForecast(dataId: number): Promise<any> {
  const payload = { data_id: dataId };
  const response = await api.post<any>('/services/recalculate-forecast', payload);
  return response.data;
}


export async function getBatteryControlSchedules(refresh: boolean = false): Promise<BatteryControlSchedule[]> {
  const response = await api.get<BatteryControlSchedule[] | {}>(`/services/battery-control-schedules?refresh=${refresh}`);
  // Handle case where backend returns empty dict {} instead of empty array
  return Array.isArray(response.data) ? response.data : [];
}

export async function getBatteryControlScheduleRecommendations(refresh: boolean = false): Promise<BatteryRecommendation[]> {
  const response = await api.get<BatteryRecommendation[] | {}>(`/services/battery-control-schedule-recommendations?refresh=${refresh}`);
  // Handle case where backend returns empty dict {} instead of empty array
  return Array.isArray(response.data) ? response.data : [];
}

export async function getManualOverrides(dataId?: number): Promise<{ overrides: BatteryControlSchedule[] }> {
  const query = dataId ? `?data_id=${dataId}` : '';
  const response = await api.get<{ overrides: BatteryControlSchedule[] }>(`/services/battery-schedule-overrides${query}`);
  return response.data;
}

export async function createManualOverride(overrideData: {
  start: string;
  end: string;
  action: 'charge' | 'discharge' | 'hold';
  target_level?: number;
}): Promise<any> {
  const response = await api.post<any>('/services/battery-schedule-overrides', overrideData);
  return response.data;
}

export async function updateManualOverride(overrideId: number, overrideData: {
  start: string;
  end: string;
  action: 'charge' | 'discharge' | 'hold';
  target_level?: number;
}): Promise<any> {
  const response = await api.put<any>(`/services/battery-schedule-overrides/${overrideId}`, overrideData);
  return response.data;
}

export async function deleteManualOverride(overrideId: number): Promise<any> {
  const response = await api.delete<any>(`/services/battery-schedule-overrides/${overrideId}`);
  return response.data;
}

export async function exportSettings(): Promise<any> {
  const response = await api.get<any>('/settings/export', {
    responseType: 'blob'
  });
  return response.data;
}

export async function importSettings(file: File): Promise<any> {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await api.post<any>('/settings/import', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
}

export async function getLatestChartData(): Promise<any> {
  const response = await api.get<any>('/users/me/chart-data');
  return response.data;
}

export async function getChartData(dataId: number): Promise<any> {
  const response = await api.get<any>(`/users/me/chart-data/${dataId}`);
  return response.data;
}

export async function exportServicesData(): Promise<any> {
  const response = await api.get<any>('/services/export', {
    responseType: 'blob'
  });
  return response.data;
}

export async function importServicesData(file: File): Promise<any> {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await api.post<any>('/services/import', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
}

export async function getRealtimeStatus(): Promise<any> {
  const response = await api.get<any>('/realtime/status');
  return response.data;
}

export async function getRealtimeData(): Promise<any> {
  const response = await api.get<any>('/realtime/data');
  return response.data;
}

export async function enableRealtimeData(): Promise<any> {
  const response = await api.post<any>('/realtime/enable', {});
  return response.data;
}

export async function disableRealtimeData(): Promise<any> {
  const response = await api.post<any>('/realtime/disable', {});
  return response.data;
}


export async function getAlerts(unacknowledgedOnly: boolean = false): Promise<AlertsResponse> {
  const response = await api.get<AlertsResponse>(`/alerts${unacknowledgedOnly ? '?unacknowledged_only=true' : ''}`);
  return response.data;
}

export async function acknowledgeAlert(alertId: number): Promise<any> {
  const response = await api.post<any>(`/alerts/${alertId}/acknowledge`, {});
  return response.data;
}

const apiService = {
  getServiceStatus,
  getAmberData,
  getSolarWebData,
  getSolcastData,
  getOpenMeteoData,
  getCurrentUser,
  resetSettings,
  logout,
  refreshToken,
  getSchedulerStatus,
  triggerSchedulerJob,
  getGridExportStatus,
  enableGridExportControl,
  disableGridExportControl,
  triggerGridExportControl,
  getGoogleAuthUrl,
  getApiLogs,
  getSettings,
  updateSetting,
  checkSettingsComplete,
  getLatestUserData,
  getDataVersion,
  getUserDataEntries,
  getUserDataById,
  recalculateForecast,
  getBatteryControlSchedules,
  getBatteryControlScheduleRecommendations,
  exportSettings,
  importSettings,
  exportServicesData,
  importServicesData,
  getLatestChartData,
  getChartData,
  getRealtimeStatus,
  getRealtimeData,
  enableRealtimeData,
  disableRealtimeData,
  getAlerts,
  acknowledgeAlert,
};

export default apiService; 